/**
 * Centralised icon module.
 *
 * Re-exports the lucide-react icons we use across the app under
 * semantic, domain-aware names so callers don't have to remember
 * which lucide glyph maps to which feature.
 *
 * Why a single map: nav menus, page headers, and stat-card chips
 * should all reach for the same icon for the same concept. Renaming
 * here updates every surface at once.
 */
import {
  // Layout / chrome
  LayoutGrid,
  Menu,
  Search,
  Bell,
  ChevronRight,
  MoreHorizontal,
  Plus,
  Sun,
  Moon,
  ArrowUp,
  ArrowDown,
  // Admin nav
  Users,
  Package,
  GitBranch,
  Layers,
  Rocket,
  Mail,
  Download,
  Scale,
  LineChart,
  Settings,
  // Tenant nav / shared
  Archive,
  Cpu,
  Globe,
  FileText,
  Palette,
  Shield,
  Database,
  Folder,
  Inbox,
  Home,
  HardDrive,
  MemoryStick,
  LogOut,
  UserCircle,
  Code,
} from 'lucide-react'

import type { LucideIcon } from 'lucide-react'

export type Icon = LucideIcon

/**
 * Nav item label → icon mapping.
 *
 * Keys MUST match the `name` field on each NavItem in the sidebars.
 * Add new entries here when adding new nav items; sidebars look up
 * by name so missing entries fall through to a generic placeholder.
 */
export const navIcons: Record<string, LucideIcon> = {
  // Admin
  Dashboard: LayoutGrid,
  Clients: Users,
  Users: Users,
  Packages: Package,
  Resellers: GitBranch,
  'Image Catalog': Layers,
  'Image catalog': Layers,
  'Security Updates': Shield,
  'WP Fleet': Rocket,
  'WP fleet': Rocket,
  Mail: Inbox,
  Migrations: Download,
  GDPR: Scale,
  Observability: LineChart,
  Settings: Settings,
  // Tenant
  Container: Package,
  Backups: Archive,
  Runtime: Cpu,
  DNS: Globe,
  Email: Mail,
  WordPress: FileText,
  Branding: Palette,
  Privacy: Shield,
  Domains: Globe,
  Databases: Database,
  Files: Folder,
  // Plan 18 A3: SFTP info page. Reuse HardDrive — it reads as "remote
  // disk access" which is what SFTP does.
  SFTP: HardDrive,
  PHP: Code,
  Account: UserCircle,
}

export {
  Archive,
  LogOut,
  LayoutGrid,
  Menu,
  Search,
  Bell,
  ChevronRight,
  MoreHorizontal,
  Plus,
  Sun,
  Moon,
  ArrowUp,
  ArrowDown,
  Home,
  Users,
  Package,
  GitBranch,
  Layers,
  Rocket,
  Mail,
  Download,
  Scale,
  LineChart,
  Settings,
  Cpu,
  Globe,
  FileText,
  Palette,
  Shield,
  Database,
  Folder,
  Inbox,
  HardDrive,
  MemoryStick,
}
